import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { withRouter } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import * as installationActions from '../../actions/installation';
import { useUrlFilters } from '../../hooks/useUrlFilters';
import MikaPageHeader from '../../components/MikaPageHeader/MikaPageHeader';
import MikaTable, { MikaColumn, MikaAction } from '../../components/MikaTable/MikaTable';
import MikaDateFilter, { DateRange } from '../../components/MikaDateFilter/MikaDateFilter';
import MikaViewToggle from '../../components/MikaViewToggle/MikaViewToggle';
import MikaExport from '../../components/MikaExport/MikaExport';
import ContactDashboardStrip from '../Contact/ContactDashboardStrip';
import PaginationComponent from '../../components/Pagination/PaginationComponent';
import InstallationMapView from './InstallationMapView';
import InstallationCardGrid from './InstallationCardGrid';
import { INSTALLATION_STATUS_COLORS, isMobile } from '../../constants/constants';
import { InstallationStats, Pagination } from '../../reducers/installation';
import '../Contact/Contact.scss';

const VIEW_STORAGE_KEY = 'mikasoft_installation_view';

const fmtDate = (d: string | null): string => {
    if (!d) return '-';
    const dt = new Date(d);
    if (isNaN(dt.getTime())) return d;
    return `${String(dt.getDate()).padStart(2,'0')}/${String(dt.getMonth()+1).padStart(2,'0')}/${dt.getFullYear()}`;
};
const fmtDateTime = (d: string | null): string => {
    if (!d) return '-';
    const dt = new Date(d);
    if (isNaN(dt.getTime())) return d;
    return `${String(dt.getDate()).padStart(2,'0')}/${String(dt.getMonth()+1).padStart(2,'0')}/${dt.getFullYear()} ${String(dt.getHours()).padStart(2,'0')}:${String(dt.getMinutes()).padStart(2,'0')}`;
};

const getUrgencyColor = (plannedDate: string | null): { color: string; flash: boolean } | null => {
    if (!plannedDate) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const planned = new Date(plannedDate + 'T00:00:00');
    if (isNaN(planned.getTime())) return null;
    const diffMs = planned.getTime() - today.getTime();
    const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays < 0) return { color: '#991b1b', flash: true };      // overdue — dark red, flash
    if (diffDays === 0) return { color: '#dc2626', flash: true };     // today — red, flash
    if (diffDays === 1) return { color: '#ef4444', flash: true };     // tomorrow — red, flash
    if (diffDays <= 3) return { color: '#ea580c', flash: true };      // 2-3 days — darker orange, flash
    if (diffDays <= 7) return { color: '#d97706', flash: false };     // 4-7 days — yellow/amber, no flash
    return null;  // > 7 days — normal green
};

type StripFilter = 'waiting' | 'overdue' | 'inProgress' | 'blocked' | 'partial' | 'completed' | 'withoutInstallation' | 'map';

interface Props {
    stats: InstallationStats | null;
    installations: any[];
    installationsPagination: Pagination;
    installationsTotal: number;
    installationsCities: any[];
    waitingItems: any[];
    waitingPagination: Pagination;
    waitingTotal: number;
    waitingCities: any[];
    withoutInstallationItems: any[];
    withoutInstallationPagination: Pagination;
    locations: any[];
    loading: boolean;
    companyProfile: any;
    actions: any;
    history: any;
    location: any;
}

const InstallationListPage: React.FC<Props> = (props) => {
    const intl = useIntl();
    const fm = useCallback(
        (id: string, defaultMessage: string, values?: Record<string, any>) =>
            intl.formatMessage({ id, defaultMessage }, values),
        [intl]
    );

    // Backend returns activeDuration strings like "3 dite, 22 ore, 25 min" in Albanian.
    // Parse the numeric parts and reformat using locale keys.
    const formatActiveDurationString = useCallback((val: string | null | undefined): string => {
        if (!val) return '';
        const str = String(val);
        const dayMatch = str.match(/(\d+)\s*(dit|day|Tag|giorn|día|дена)/i);
        const hourMatch = str.match(/(\d+)\s*(or|hour|Std|ore|hora|час)/i);
        const minMatch = str.match(/(\d+)\s*(min|мин)/i);
        const days = dayMatch ? parseInt(dayMatch[1]) : 0;
        const hours = hourMatch ? parseInt(hourMatch[1]) : 0;
        const minutes = minMatch ? parseInt(minMatch[1]) : 0;
        if (days === 0 && hours === 0 && minutes === 0) {
            // Nothing parsed — just return the original string.
            return str;
        }
        const parts: string[] = [];
        if (days > 0) parts.push(fm('installation.detail.duration.days', '{count} ditë', { count: days }));
        if (hours > 0) parts.push(fm('installation.detail.duration.hours', '{count} orë', { count: hours }));
        if (minutes > 0 || parts.length === 0) parts.push(fm('installation.detail.duration.minutes', '{count} min', { count: minutes }));
        return parts.join(', ');
    }, [fm]);

    const { filters, setFilter, setFilters, replaceFilters } = useUrlFilters(props.history, props.location, {
        page: { default: 1, type: 'number' },
        perPage: { default: 20, type: 'number' },
        search: { default: '', type: 'string' },
        strip: { default: 'waiting', type: 'string' },
        dateFrom: { default: '', type: 'string' },
        dateTo: { default: '', type: 'string' },
        city: { default: '', type: 'string' },
    });

    const currentPage = filters.page as number;
    const perPage = filters.perPage as number;
    const searchTerm = filters.search as string;
    const stripFilter = (filters.strip || 'waiting') as StripFilter;
    const dateRange: DateRange | null = filters.dateFrom && filters.dateTo ? { from: filters.dateFrom, to: filters.dateTo } : null;
    const activeCity = filters.city || null;

    const [viewMode, setViewMode] = useState<'table' | 'card'>(() => {
        const saved = localStorage.getItem(VIEW_STORAGE_KEY);
        return saved === 'card' ? 'card' : 'table';
    });
    const [cityPopoverOpen, setCityPopoverOpen] = useState(false);
    const [cityPopoverPos, setCityPopoverPos] = useState({ top: 0, left: 0 });
    const stripRef = useRef<HTMLDivElement>(null);

    useEffect(() => { props.actions.fetchInstallationStats(); }, []);
    useEffect(() => { fetchData(); }, [props.location.search]);
    useEffect(() => {
        if (stripFilter === 'map') props.actions.fetchInstallationLocations({});
    }, [stripFilter]);

    const fetchData = () => {
        if (stripFilter === 'map') return;

        if (stripFilter === 'waiting' || stripFilter === 'overdue') {
            const f: any = { page: currentPage, per_page: perPage };
            if (stripFilter === 'overdue') f.overdue_only = 1;
            if (searchTerm) f.search = searchTerm;
            if (activeCity) f.city = activeCity;
            props.actions.fetchWaiting(f);
        } else if (stripFilter === 'withoutInstallation') {
            props.actions.fetchWithoutInstallation({
                page: currentPage, per_page: perPage,
                search: searchTerm || undefined,
            });
        } else {
            const statusMap: Record<string, number> = { inProgress: 1, blocked: 2, partial: 3, completed: 4 };
            const f: any = { page: currentPage, per_page: perPage, status: statusMap[stripFilter] };
            if (searchTerm) f.search = searchTerm;
            if (activeCity) f.city = activeCity;
            if (dateRange?.from) f.start_date = dateRange.from;
            if (dateRange?.to) f.end_date = dateRange.to;
            props.actions.fetchInstallations(f);
        }
    };

    const isWaitingView = stripFilter === 'waiting' || stripFilter === 'overdue';
    const isWithoutInstallation = stripFilter === 'withoutInstallation';
    const effectiveView: 'table' | 'card' = isMobile ? 'table' : viewMode;
    const tableData = isWaitingView ? props.waitingItems : isWithoutInstallation ? props.withoutInstallationItems : props.installations;
    const tablePagination = isWaitingView ? props.waitingPagination : isWithoutInstallation ? props.withoutInstallationPagination : props.installationsPagination;
    const totalValue = isWaitingView ? props.waitingTotal : props.installationsTotal;
    const cities = isWaitingView ? props.waitingCities : props.installationsCities;

    const dashboardStats = useMemo(() => {
        const s = props.stats;
        if (!s) return [];
        return [
            { key: 'waiting', label: fm('installation.strip.waiting', 'Në pritje'), value: s.waiting ?? 0, icon: 'alert' },
            { key: 'overdue', label: fm('installation.strip.overdue', 'Të vonuara'), value: s.overdue ?? 0, icon: 'alert' },
            { key: 'inProgress', label: fm('installation.strip.inProgress', 'Në proces'), value: s.inProgress ?? 0, icon: 'box' },
            { key: 'blocked', label: fm('installation.strip.blocked', 'E bllokuar'), value: s.blocked ?? 0, icon: 'alert' },
            { key: 'partial', label: fm('installation.strip.partial', 'Pjesërisht'), value: s.partial ?? 0, icon: 'layers' },
            { key: 'completed', label: fm('installation.strip.completed', 'E përfunduar'), value: s.completed ?? 0, icon: 'check' },
            { key: 'withoutInstallation', label: fm('installation.strip.withoutInstallation', 'Pa instalim'), value: s.withoutInstallationWaiting ?? 0, icon: 'folder' },
            { key: 'cities', label: fm('installation.strip.cities', 'Qytetet'), value: cities.length, icon: 'map' },
            { key: 'map', label: fm('installation.strip.map', 'Harta'), value: '', icon: 'map' },
        ];
    }, [props.stats, cities, fm]);

    const positionPopoverUnderCard = (idx: number) => {
        if (!stripRef.current) return { top: 0, left: 0 };
        const cards = stripRef.current.querySelectorAll('.cds-card');
        if (cards[idx]) { const r = cards[idx].getBoundingClientRect(); return { top: r.bottom + 4, left: r.left }; }
        return { top: 0, left: 0 };
    };

    const handleStripClick = (key: string) => {
        if (key === 'cities') {
            setCityPopoverPos(positionPopoverUnderCard(7));
            setCityPopoverOpen(!cityPopoverOpen);
            return;
        }
        setCityPopoverOpen(false);
        if (key !== stripFilter) {
            setFilters({ strip: key, city: '', page: 1 });
        } else {
            setFilters({ strip: key, page: 1 });
        }
    };

    const handleRowClick = (row: any) => {
        if (isWaitingView) {
            if (row.source === 'warehouse') {
                props.history.push(`/dashboard/installation/create?wr=${row.warehouseRecordId}`);
            } else {
                props.history.push(`/dashboard/installation/create?ti=${row.transportItemId || row.id}`);
            }
        } else if (isWithoutInstallation) {
            if (row.install_status === 'completed' && row.installation_id) {
                props.history.push(`/dashboard/installation/${row.installation_id}`);
            } else if (row.source === 'warehouse') {
                props.history.push(`/dashboard/installation/create-without-installation?wr=${row.warehouse_record_id || row.id}`);
            } else {
                props.history.push(`/dashboard/installation/create-without-installation?ti=${row.id}`);
            }
        } else {
            props.history.push(`/dashboard/installation/${row.id}`);
        }
    };

    // Waiting columns
    const waitingColumns: MikaColumn[] = useMemo(() => [
        { key: 'warehouseLabel', title: fm('installation.col.label', 'Etiketa'), sortable: true, width: '120px' },
        { key: 'clientName', title: fm('production.detail.client', 'Klienti'), render: (_v: any, row: any) => row.client?.name || '-' },
        { key: 'projectName', title: fm('production.detail.project', 'Projekti'), render: (_v: any, row: any) => row.project?.name || '-' },
        { key: 'clientCity', title: fm('installation.col.city', 'Qyteti'), hideOnMobile: true, render: (_v: any, row: any) => row.client?.city || <span style={{ color: '#d0dbe8' }}>—</span> },
        { key: 'clientCountry', title: fm('installation.col.country', 'Shteti'), hideOnMobile: true, render: (_v: any, row: any) => row.client?.country || <span style={{ color: '#d0dbe8' }}>—</span> },
        { key: 'products', title: fm('installation.col.products', 'Produkte'), width: '80px', render: (val: any) => (
            <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600, color: '#1e3a5f', background: '#dbeafe' }}>{val?.length || 0}</span>
        )},
        { key: 'productValue', title: fm('installation.col.value', 'Vlera'), hideOnMobile: true, render: (val: number) => val ? `€${val.toFixed(2)}` : '-' },
        { key: 'plannedDate', title: fm('installation.col.plannedDate', 'Data e planifikuar'), sortable: true, render: (val: string, row: any) => {
            if (!val) return <span style={{ color: '#d0dbe8' }}>—</span>;
            const urgency = getUrgencyColor(val);
            const color = urgency ? urgency.color : '#22c55e';
            return (
                <span style={{ color, fontWeight: 700, animation: urgency?.flash ? 'urgencyPulse 1.8s ease-in-out infinite' : 'none' }}>{fmtDate(val)}</span>
            );
        }},
        { key: 'deliveredAt', title: fm('installation.col.deliveredAt', 'Dorëzuar me'), hideOnMobile: true, render: (val: string) => fmtDateTime(val) },
        // eslint-disable-next-line react-hooks/exhaustive-deps
    ], [intl]);

    // Installation columns
    const installationColumns: MikaColumn[] = useMemo(() => [
        { key: 'clientName', title: fm('production.detail.client', 'Klienti'), render: (_v: any, row: any) => row.client?.name || '-' },
        { key: 'projectName', title: fm('production.detail.project', 'Projekti'), render: (_v: any, row: any) => row.project?.name || '-' },
        { key: 'status', title: fm('installation.col.status', 'Statusi'), width: '120px', render: (val: number, row: any) => {
            const colors = INSTALLATION_STATUS_COLORS[val] || { color: '#94a3b8', bg: '#f1f5f9' };
            return <span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: 12, fontSize: 10, fontWeight: 600, color: colors.color, background: colors.bg }}>{row.statusLabel}</span>;
        }},
        { key: 'installerName', title: fm('installation.col.installer', 'Instaluesi'), hideOnMobile: true, render: (_v: any, row: any) => {
            if (row.isOutsourced) return <span>{row.outsourcedCompany} <span style={{ fontSize: 9, color: '#f97316', fontWeight: 600 }}>{fm('installation.installer.outsourced', 'E JASHTME')}</span></span>;
            return row.installer?.name || '-';
        }},
        { key: 'installationStart', title: fm('installation.col.start', 'Fillimi'), hideOnMobile: true, render: (val: string) => fmtDateTime(val) },
        { key: 'installationEnd', title: fm('installation.col.end', 'Përfundimi'), hideOnMobile: true, render: (val: string) => val ? fmtDateTime(val) : <span style={{ color: '#d0dbe8' }}>—</span> },
        { key: 'activeDuration', title: fm('installation.col.activeDuration', 'Koha aktive'), hideOnMobile: true, render: (val: string) => formatActiveDurationString(val) },
        { key: 'productValue', title: fm('installation.col.value', 'Vlera'), hideOnMobile: true, render: (val: number) => val ? `€${val.toFixed(2)}` : '-' },
        { key: 'blockCount', title: fm('installation.col.blockCount', 'Bllokime'), hideOnMobile: true, width: '80px', render: (val: number) => val > 0 ? <span style={{ color: '#ef4444', fontWeight: 700 }}>{fm('installation.blockCount.value', '{count}x', { count: val })}</span> : null },
        { key: 'blockReason', title: fm('installation.col.blockReason', 'Arsyeja'), hideOnMobile: true, render: (val: string, row: any) => {
            if (row.status !== 2 || !val) return null;
            return <span style={{ color: '#ef4444', fontSize: 11 }}>{val.length > 30 ? val.substring(0, 30) + '...' : val}</span>;
        }},
        // eslint-disable-next-line react-hooks/exhaustive-deps
    ], [intl]);

    // Without installation columns (Pa instalim)
    const withoutInstallationColumns: MikaColumn[] = useMemo(() => [
        { key: 'label', title: fm('installation.col.label', 'Etiketa'), sortable: true, width: '120px' },
        { key: 'client_name', title: fm('production.detail.client', 'Klienti'), render: (val: any, row: any) => val || '-' },
        { key: 'project_name', title: fm('production.detail.project', 'Projekti'), render: (val: any, row: any) => val || '-' },
        { key: 'client_city', title: fm('installation.col.city', 'Qyteti'), hideOnMobile: true,
            render: (val: any) => val || <span style={{ color: '#d0dbe8' }}>—</span> },
        { key: 'client_country', title: fm('installation.col.country', 'Shteti'), hideOnMobile: true,
            render: (val: any) => val || <span style={{ color: '#d0dbe8' }}>—</span> },
        { key: 'install_status', title: fm('installation.col.status', 'Statusi'), width: '120px',
            render: (val: string) => {
                const isCompleted = val === 'completed';
                return (
                    <span style={{
                        display: 'inline-block', padding: '3px 10px', borderRadius: 12,
                        fontSize: 10, fontWeight: 600,
                        color: isCompleted ? '#16a34a' : '#d97706',
                        background: isCompleted ? '#f0fdf4' : '#fffbeb',
                    }}>
                        {isCompleted ? fm('installation.status.completed', 'E përfunduar') : fm('installation.status.waiting', 'Në pritje')}
                    </span>
                );
            }
        },
        { key: 'source', title: fm('installation.col.source', 'Burimi'), hideOnMobile: true, width: '90px',
            render: (val: string) => val === 'transport' ? fm('installation.source.transport', 'Transport') : fm('installation.source.direct', 'Direkt') },
        { key: 'created_at', title: fm('installation.col.date', 'Data'), sortable: true, hideOnMobile: true, width: '100px',
            render: (val: string) => fmtDate(val) },
        // eslint-disable-next-line react-hooks/exhaustive-deps
    ], [intl]);

    const columns = isWaitingView ? waitingColumns : isWithoutInstallation ? withoutInstallationColumns : installationColumns;

    // Translate numeric installation status (1=inProgress, 2=blocked, 3=partial, 4=completed).
    // Backend-provided `statusLabel` is always Albanian; we map on the client.
    const getInstallationStatusLabel = (row: any) => {
        if (row?.status === 1) return fm('installation.strip.inProgress', 'Në proces');
        if (row?.status === 2) return fm('installation.strip.blocked', 'E bllokuar');
        if (row?.status === 3) return fm('installation.strip.partial', 'Pjesërisht');
        if (row?.status === 4) return fm('installation.strip.completed', 'E përfunduar');
        return row?.statusLabel || '';
    };

    const exportColumns = isWaitingView
        ? [
            { key: 'warehouseLabel', title: fm('installation.col.label', 'Etiketa') },
            { key: 'client',         title: fm('production.detail.client', 'Klienti'),  render: (row: any) => row.client?.name || '' },
            { key: 'project',        title: fm('production.detail.project', 'Projekti'), render: (row: any) => row.project?.name || '' },
            { key: 'clientCity',     title: fm('installation.col.city', 'Qyteti'),       render: (row: any) => row.client?.city || '' },
            { key: 'clientCountry',  title: fm('installation.col.country', 'Shteti'),    render: (row: any) => row.client?.country || '' },
            { key: 'products',       title: fm('installation.col.products', 'Produkte'), render: (row: any) => String(row.products?.length || 0) },
            { key: 'productValue',   title: fm('installation.col.value', 'Vlera'),       render: (row: any) => row.productValue ? `€${Number(row.productValue).toFixed(2)}` : '' },
            { key: 'plannedDate',    title: fm('installation.col.plannedDate', 'Data e planifikuar'), render: (row: any) => fmtDate(row.plannedDate) },
            { key: 'deliveredAt',    title: fm('installation.col.deliveredAt', 'Dorëzuar me'),        render: (row: any) => fmtDateTime(row.deliveredAt) },
        ]
        : isWithoutInstallation
        ? [
            { key: 'label',          title: fm('installation.col.label', 'Etiketa') },
            { key: 'client_name',    title: fm('production.detail.client', 'Klienti') },
            { key: 'project_name',   title: fm('production.detail.project', 'Projekti') },
            { key: 'client_city',    title: fm('installation.col.city', 'Qyteti') },
            { key: 'client_country', title: fm('installation.col.country', 'Shteti') },
            { key: 'install_status', title: fm('installation.col.status', 'Statusi'),
                render: (row: any) => row.install_status === 'completed'
                    ? fm('installation.status.completed', 'E përfunduar')
                    : fm('installation.status.waiting', 'Në pritje') },
            { key: 'source',         title: fm('installation.col.source', 'Burimi'),
                render: (row: any) => row.source === 'transport'
                    ? fm('installation.source.transport', 'Transport')
                    : fm('installation.source.direct', 'Direkt') },
            { key: 'created_at',     title: fm('installation.col.date', 'Data'), render: (row: any) => fmtDate(row.created_at) },
        ]
        : [
            { key: 'client',            title: fm('production.detail.client', 'Klienti'),  render: (row: any) => row.client?.name || '' },
            { key: 'project',           title: fm('production.detail.project', 'Projekti'), render: (row: any) => row.project?.name || '' },
            { key: 'status',            title: fm('installation.col.status', 'Statusi'),    render: (row: any) => getInstallationStatusLabel(row) },
            { key: 'installer',         title: fm('installation.col.installer', 'Instaluesi'),
                render: (row: any) => row.isOutsourced ? (row.outsourcedCompany || '') : (row.installer?.name || '') },
            { key: 'installationStart', title: fm('installation.col.start', 'Fillimi'),        render: (row: any) => fmtDateTime(row.installationStart) },
            { key: 'installationEnd',   title: fm('installation.col.end', 'Përfundimi'),       render: (row: any) => row.installationEnd ? fmtDateTime(row.installationEnd) : '' },
            { key: 'activeDuration',    title: fm('installation.col.activeDuration', 'Koha aktive') },
            { key: 'productValue',      title: fm('installation.col.value', 'Vlera'),          render: (row: any) => row.productValue ? `€${Number(row.productValue).toFixed(2)}` : '' },
            { key: 'blockCount',        title: fm('installation.col.blockCount', 'Bllokime'),  render: (row: any) => row.blockCount ? String(row.blockCount) : '' },
            { key: 'blockReason',       title: fm('installation.col.blockReason', 'Arsyeja'),  render: (row: any) => (row.status === 2 && row.blockReason) ? row.blockReason : '' },
        ];

    const tableActions: MikaAction[] = isWaitingView ? [] : [
        { icon: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>, tooltip: fm('installation.action.view', 'Shiko'), onClick: handleRowClick },
    ];

    return (
        <div className="clp-page">
            <MikaPageHeader
                title={fm('installation.title', 'Instalimi')}
                subtitle={stripFilter !== 'map'
                    ? `${isWaitingView
                        ? fm('installation.subtitle.waiting', '{count} në pritje', { count: tablePagination.total })
                        : fm('installation.subtitle.installations', '{count} instalime', { count: tablePagination.total })
                    }${totalValue > 0 ? ` · ${fm('installation.subtitle.value', 'Vlera: €{value}', { value: totalValue.toFixed(2) })}` : ''}`
                    : fm('installation.subtitle.map', 'Harta')}
                actions={
                    <button onClick={() => props.history.push('/dashboard/installation/create')}
                        style={{ padding: '7px 16px', borderRadius: 6, border: '1px solid rgba(255,255,255,0.25)', background: 'rgba(255,255,255,0.12)', color: '#fff', fontSize: 12, fontWeight: 500, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, transition: 'background 0.15s' }}
                        onMouseEnter={(e) => { (e.currentTarget).style.background = 'rgba(255,255,255,0.22)'; }}
                        onMouseLeave={(e) => { (e.currentTarget).style.background = 'rgba(255,255,255,0.12)'; }}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                        <FormattedMessage id="installation.createButton" defaultMessage="Krijo Instalim" />
                    </button>
                }
            />

            <div className="clp-compact-toolbar">
                <div className="clp-search-wrap">
                    <svg className="clp-search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
                    <input className="clp-search-input" type="text" placeholder={isWaitingView ? fm('installation.search.waiting', 'Kërko në pritje...') : fm('installation.search.installations', 'Kërko instalime...')} value={searchTerm} onChange={(e) => replaceFilters({ search: e.target.value, page: 1 })} />
                    {searchTerm && <button className="clp-search-clear" onClick={() => replaceFilters({ search: '', page: 1 })}><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg></button>}
                </div>
                <div className="clp-toolbar-actions">
                    {!isWaitingView && stripFilter !== 'map' && <MikaDateFilter value={dateRange} onChange={(r) => setFilters({ dateFrom: r?.from || '', dateTo: r?.to || '', page: 1 })} />}
                    {!isMobile && stripFilter !== 'map' && <MikaViewToggle mode={viewMode} onChange={(m) => { setViewMode(m); localStorage.setItem(VIEW_STORAGE_KEY, m); }} />}
                    <MikaExport fetchAllData={async () => {
                        if (isWaitingView) {
                            const f: any = { per_page: 0 };
                            if (stripFilter === 'overdue') f.overdue_only = 1;
                            if (searchTerm) f.search = searchTerm;
                            if (activeCity) f.city = activeCity;
                            const res = await props.actions.fetchWaiting(f);
                            return res?.data || tableData;
                        } else {
                            const statusMap: Record<string, number> = { inProgress: 1, blocked: 2, partial: 3, completed: 4 };
                            const f: any = { per_page: 0, status: statusMap[stripFilter] };
                            if (searchTerm) f.search = searchTerm;
                            if (activeCity) f.city = activeCity;
                            const res = await props.actions.fetchInstallations(f);
                            return res?.data || tableData;
                        }
                    }} columns={exportColumns} fileName={fm('installation.exportFilename', 'instalime')} title={fm('installation.exportTitle', 'Instalime')} companyProfile={props.companyProfile} />
                </div>
            </div>

            <div ref={stripRef}>
                <ContactDashboardStrip stats={dashboardStats} activeFilter={stripFilter} onFilterClick={handleStripClick} />
            </div>

            {cityPopoverOpen && (
                <div className="clp-city-popover" style={{ top: cityPopoverPos.top, left: cityPopoverPos.left }}>
                    <div className="clp-city-header"><FormattedMessage id="installation.cityPopover.header" defaultMessage="Qytetet" /></div>
                    <button className="clp-city-row" onClick={() => { setFilters({ city: '', page: 1 }); setCityPopoverOpen(false); }}>
                        <span className="clp-city-name"><FormattedMessage id="common.pagination.all" defaultMessage="Të gjitha" /></span>
                    </button>
                    {(cities || []).map((c: any) => (
                        <button key={c.city} className={`clp-city-row ${activeCity === c.city ? 'clp-city-row-active' : ''}`}
                            onClick={() => { setFilters({ city: c.city, page: 1 }); setCityPopoverOpen(false); }}>
                            <span className="clp-city-name">{c.city}</span>
                            <span className="clp-city-count">{c.count}</span>
                        </button>
                    ))}
                </div>
            )}

            {activeCity && (
                <div className="clp-active-filter-bar">
                    <div className="clp-active-filter-chip">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                        <span>{activeCity}</span>
                        <button className="clp-active-filter-clear" onClick={() => setFilters({ city: '', page: 1 })}>
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                        </button>
                    </div>
                </div>
            )}

            {stripFilter !== 'map' && effectiveView === 'table' && (
                <MikaTable columns={columns} data={tableData} actions={tableActions} loading={props.loading}
                    onRowClick={handleRowClick}
                    emptyMessage={isWaitingView ? fm('installation.empty.waiting', 'Nuk ka produkte në pritje për instalim') : isWithoutInstallation ? fm('installation.empty.withoutInstallation', 'Nuk ka produkte pa instalim') : fm('installation.empty.installations', 'Nuk ka instalime')} />
            )}
            {stripFilter !== 'map' && effectiveView === 'card' && (
                <InstallationCardGrid
                    data={tableData}
                    onEdit={handleRowClick}
                    mode={isWaitingView ? 'waiting' : isWithoutInstallation ? 'withoutInstallation' : 'installation'}
                />
            )}

            {stripFilter === 'map' && (
                <InstallationMapView
                    locations={props.locations}
                    onMarkerClick={(item: any) => {
                        if (item.installationId) props.history.push(`/dashboard/installation/${item.installationId}`);
                    }}
                />
            )}

            {stripFilter !== 'map' && (
                <PaginationComponent currentPage={currentPage} perPage={perPage} pagination={tablePagination}
                    onPageChange={(p: number) => setFilter('page', p)} onPerPageChange={(pp: number) => setFilters({ perPage: pp, page: 1 })}
                    label={isWaitingView ? fm('installation.recordsLabel.waiting', 'në pritje') : fm('installation.recordsLabel.installations', 'instalime')} />
            )}
        </div>
    );
};

const mapStateToProps = (state: any) => ({
    stats: state.installation.stats,
    installations: state.installation.installations,
    installationsPagination: state.installation.installationsPagination,
    installationsTotal: state.installation.installationsTotal,
    installationsCities: state.installation.installationsCities,
    waitingItems: state.installation.waitingItems,
    waitingPagination: state.installation.waitingPagination,
    waitingTotal: state.installation.waitingTotal,
    waitingCities: state.installation.waitingCities,
    withoutInstallationItems: state.installation.withoutInstallationItems,
    withoutInstallationPagination: state.installation.withoutInstallationPagination,
    locations: state.installation.locations,
    loading: state.app.loading,
    companyProfile: state.app.companyProfile,
});

const mapDispatchToProps = (dispatch: any) => ({
    actions: bindActionCreators(installationActions as any, dispatch),
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(InstallationListPage) as any) as any;
